import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";

/**
 * The consolidated Plate Talk shell has a lower workspace whose top edge can
 * move as the chat pane is expanded/collapsed. Dialog portals normally mount
 * under document.body, so they need a little bit of information about that
 * edge in order not to paint over the pinned conversation.
 *
 * This provider deliberately does not create a layout box. It can therefore
 * wrap the complete shell (including the pinned conversation) without
 * changing the shell's flex sizing. Keeping the provider above both surfaces
 * is important: a dialog opened by the conversation must still know where the
 * lower workspace begins:
 *
 *   <LowerWorkspaceDialogProvider>
 *     <div className="shell">
 *       <section data-lower-workspace-boundary>...</section>
 *     </div>
 *   </LowerWorkspaceDialogProvider>
 *
 * The first element child is used when the boundary attribute is omitted.
 */

export interface LowerWorkspaceDialogContainment {
  contained: boolean;
  boundaryTop: number | null;
  overlayStyle?: CSSProperties;
  contentStyle?: CSSProperties;
}

const Uncontained: LowerWorkspaceDialogContainment = {
  contained: false,
  boundaryTop: null,
};

const LowerWorkspaceDialogContext =
  createContext<LowerWorkspaceDialogContainment | null>(null);

function containmentForTop(
  boundaryTop: number | null,
): LowerWorkspaceDialogContainment {
  if (boundaryTop === null) return Uncontained;

  const top = `${boundaryTop}px`;
  return {
    contained: true,
    boundaryTop,
    // Radix still owns the modal/focus behavior. Only its visual region is
    // constrained, leaving the pinned conversation visible above the edge.
    overlayStyle: {
      top,
      right: 0,
      bottom: 0,
      left: 0,
    },
    contentStyle: {
      top: `calc(${top} + (100dvh - ${top}) / 2)`,
      maxHeight: `calc(100dvh - ${top} - 2rem)`,
      overflowY: "auto",
    },
  };
}

export interface LowerWorkspaceDialogProviderProps {
  children: ReactNode;
  /**
   * Optional ref for shells that already keep a ref on their lower section.
   * Otherwise the provider finds [data-lower-workspace-boundary], falling
   * back to its first element child.
   */
  boundaryRef?: RefObject<HTMLElement | null>;
}

export function LowerWorkspaceDialogProvider({
  children,
  boundaryRef,
}: LowerWorkspaceDialogProviderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [boundaryTop, setBoundaryTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let boundary: HTMLElement | null = null;
    const findBoundary = () => {
      boundary =
        boundaryRef?.current ??
        root.querySelector<HTMLElement>("[data-lower-workspace-boundary]") ??
        (root.firstElementChild instanceof HTMLElement
          ? root.firstElementChild
          : null);
      return boundary;
    };

    const measure = () => {
      const element = findBoundary();
      if (!element) {
        setBoundaryTop(null);
        return;
      }
      const nextTop = Math.max(0, Math.round(element.getBoundingClientRect().top * 100) / 100);
      setBoundaryTop((current) => (current === nextTop ? current : nextTop));
    };

    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("scroll", measure);

    const ResizeObserverConstructor = window.ResizeObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(measure)
      : null;
    const element = findBoundary();
    if (element) resizeObserver?.observe(element);

    // The shell changes the lower section's position by changing the chat
    // section's classes. A resize observer catches most of those changes; the
    // mutation observer covers the class-only case without polling.
    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(measure)
        : null;
    mutationObserver?.observe(root, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("scroll", measure);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [boundaryRef]);

  const value = useMemo(
    () => containmentForTop(boundaryTop),
    [boundaryTop],
  );

  return (
    <LowerWorkspaceDialogContext.Provider value={value}>
      <div
        ref={rootRef}
        style={{
          display: "contents",
          "--lower-workspace-top":
            boundaryTop === null ? undefined : `${boundaryTop}px`,
        } as CSSProperties}
      >
        {children}
      </div>
    </LowerWorkspaceDialogContext.Provider>
  );
}

/**
 * Hook consumed by shared dialog primitives and by intentionally hand-rolled
 * shell panels. Outside this provider it returns the uncontained standard
 * behavior.
 */
export function useLowerWorkspaceDialogContainment() {
  const localContainment = useContext(LowerWorkspaceDialogContext);
  return localContainment ?? Uncontained;
}

/**
 * Global fixed modals (for example the barcode scanner mounted beside the
 * router) are not React descendants of the shell provider. They still need
 * the same visual boundary when a consolidated shell is present. Outside that
 * shell there is no boundary marker, so this remains the normal viewport
 * behavior.
 */
export function useGlobalLowerWorkspaceDialogContainment() {
  const [boundaryTop, setBoundaryTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const boundary = document.querySelector<HTMLElement>(
        "[data-lower-workspace-boundary]",
      );
      if (!boundary) {
        setBoundaryTop(null);
        return;
      }
      const nextTop = Math.max(0, Math.round(boundary.getBoundingClientRect().top * 100) / 100);
      setBoundaryTop((current) => (current === nextTop ? current : nextTop));
    };

    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("scroll", measure);
    const resizeObserver = window.ResizeObserver
      ? new window.ResizeObserver(measure)
      : null;
    const boundary = document.querySelector<HTMLElement>(
      "[data-lower-workspace-boundary]",
    );
    if (boundary) resizeObserver?.observe(boundary);

    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(measure)
        : null;
    mutationObserver?.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("scroll", measure);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);

  return useMemo(
    () => containmentForTop(boundaryTop),
    [boundaryTop],
  );
}

/**
 * Small focus utility for legacy panels that cannot use Radix Dialog directly
 * (for example, a data-heavy side drawer). Shared Dialog and AlertDialog
 * already provide this behavior through Radix's FocusScope.
 */
export function useDialogFocusTrap<T extends HTMLElement>(
  panelRef: RefObject<T | null>,
  onClose?: () => void,
  active = true,
) {
  const openerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!active) return;
    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const focusPanel = () => {
      if (!panel || !panel.isConnected) return;
      const firstFocusable = panel.querySelector<HTMLElement>(
        "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      );
      (firstFocusable ?? panel).focus();
    };
    const frame = window.requestAnimationFrame(focusPanel);

    return () => {
      window.cancelAnimationFrame(frame);
      const opener = openerRef.current;
      if (opener?.isConnected) {
        window.requestAnimationFrame(() => opener.focus());
      }
    };
  }, [active, panelRef]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<T>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose, panelRef],
  );

  return { onKeyDown };
}

// Short aliases make the wrapper convenient for the shell while retaining the
// descriptive name for dialog consumers.
export const LowerWorkspaceProvider = LowerWorkspaceDialogProvider;
export const BottomShellProvider = LowerWorkspaceDialogProvider;
